/**
 * useDedicatedServerLogs — Logs de provisioning (mode static OU live SSE).
 *
 * - status === 'installing' → SSE sur `/provisioning-logs/stream`
 * - sinon → `useCustom` paginé sur `/provisioning-logs`
 *
 * @spec specs/app/admin/dedicated-servers.md §7.3
 */

import { useCustom } from '@refinedev/core';
import { useEffect, useMemo, useState } from 'react';
import type { ProvisioningLog, ServerStatus } from '@/types/dedicatedServer';

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? '';

interface UseLogsResult {
  logs: ProvisioningLog[];
  isLoading: boolean;
  isStreaming: boolean;
  error: unknown;
}

interface LogsResponse {
  data?: ProvisioningLog[];
  logs?: ProvisioningLog[];
}

export function useDedicatedServerLogs(serverId: string, status: ServerStatus): UseLogsResult {
  const isLive = status === 'installing';
  const [liveLogs, setLiveLogs] = useState<ProvisioningLog[]>([]);

  // --- Mode static : useCustom sur endpoint REST ---
  const staticQuery = useCustom<LogsResponse>({
    url: `/api/v1/admin/dedicated-servers/${serverId}/provisioning-logs`,
    method: 'get',
    queryOptions: {
      enabled: !isLive,
      retry: 1,
    },
  });

  // --- Mode live : SSE EventSource (réinit à chaque changement serverId/isLive) ---
  useEffect(() => {
    if (!isLive) {
      setLiveLogs([]);
      return;
    }
    const streamUrl = `${API_URL}/api/v1/admin/dedicated-servers/${serverId}/provisioning-logs/stream`;
    let es: EventSource | null = null;
    try {
      es = new EventSource(streamUrl, { withCredentials: true });
      es.onmessage = (ev) => {
        try {
          const payload = JSON.parse(ev.data) as ProvisioningLog;
          setLiveLogs((prev) => [payload, ...prev]);
        } catch {
          /* noop — ligne SSE non-JSON ignorée */
        }
      };
      es.onerror = () => {
        // Laisser la connexion tenter un reconnect natif — pas de setState err ici.
      };
    } catch {
      es = null;
    }
    return () => {
      if (es) es.close();
    };
  }, [serverId, isLive]);

  const logs = useMemo<ProvisioningLog[]>(() => {
    if (isLive) return liveLogs;
    const body = staticQuery.data?.data as LogsResponse | ProvisioningLog[] | undefined;
    if (Array.isArray(body)) return body;
    return body?.data ?? body?.logs ?? [];
  }, [isLive, liveLogs, staticQuery.data]);

  return {
    logs,
    isLoading: !isLive && staticQuery.isLoading,
    isStreaming: isLive,
    error: staticQuery.isError ? staticQuery.error : null,
  };
}
